import { computed, defineComponent, ref, watch } from 'vue';
import classNames from '../../_util/classNames';
import { initDefaultProps } from '../../_util/props-util';
import useConfigInject from '../../config-provider/hooks/useConfigInject';
import { FormItemInputContext } from '../../form/FormItemContext';
import { getMergedStatus } from '../../_util/statusUtils';
import useStyle from '../style/otp';
import OTPInput from './OTPInput';
function strToArr(str) {
    return (str || '').split('');
}
export const otpProps = () => ({
    length: { type: Number, default: 6 },
    size: String,
    defaultValue: String,
    value: String,
    formatter: Function,
    separator: {
        type: [String, Number, Object, Function],
    },
    disabled: { type: Boolean, default: undefined },
    status: String,
    mask: { type: [Boolean, String] },
    type: String,
    autoComplete: String,
    autofocus: Boolean,
    inputMode: String,
    bordered: { type: Boolean, default: true },
    rootClassName: String,
});
const OTP = defineComponent({
    name: 'AInputOTP',
    inheritAttrs: false,
    props: initDefaultProps(otpProps(), {
        length: 6,
        bordered: true,
    }),
    emits: ['update:value', 'change', 'input', 'focus'],
    setup(props, { attrs, emit, expose }) {
        const { prefixCls, size: contextSize, direction } = useConfigInject('otp', props);
        const [wrapSSR, hashId] = useStyle(prefixCls);
        const formItemInputContext = FormItemInputContext.useInject();
        const mergedStatus = computed(() => getMergedStatus(formItemInputContext.status, props.status));
        const mergedSize = computed(() => { var _a; return (_a = props.size) !== null && _a !== void 0 ? _a : contextSize.value; });
        const format = (txt) => (props.formatter ? props.formatter(txt) : txt);
        const valueCells = ref(strToArr(format((props.value !== undefined ? props.value : props.defaultValue) || '')));
        const inputsRef = ref({});
        const containerRef = ref();
        watch(() => props.value, v => {
            if (v !== undefined) {
                valueCells.value = strToArr(v);
            }
        });
        expose({
            focus: () => { var _a, _b; return (_b = (_a = inputsRef.value[0]) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a); },
            blur: () => {
                var _a, _b;
                for (let i = 0; i < props.length; i += 1) {
                    (_b = (_a = inputsRef.value[i]) === null || _a === void 0 ? void 0 : _a.blur) === null || _b === void 0 ? void 0 : _b.call(_a);
                }
            },
            nativeElement: containerRef,
        });
        const triggerValueCellsChange = (next) => {
            const prev = valueCells.value;
            valueCells.value = next;
            emit('input', next);
            if (next.length === props.length &&
                next.every(c => c) &&
                next.some((c, i) => prev[i] !== c)) {
                const joined = next.join('');
                emit('update:value', joined);
                emit('change', joined);
            }
        };
        const patchValue = (index, txt) => {
            let nextCells = [...valueCells.value];
            for (let i = 0; i < index; i += 1) {
                if (!nextCells[i])
                    nextCells[i] = '';
            }
            if (txt.length <= 1) {
                nextCells[index] = txt;
            }
            else {
                nextCells = nextCells.slice(0, index).concat(strToArr(txt));
            }
            nextCells = nextCells.slice(0, props.length);
            for (let i = nextCells.length - 1; i >= 0; i -= 1) {
                if (nextCells[i])
                    break;
                nextCells.pop();
            }
            const formatted = format(nextCells.map(c => c || ' ').join(''));
            nextCells = strToArr(formatted).map((c, i) => {
                if (c === ' ' && !nextCells[i])
                    return nextCells[i];
                return c;
            });
            return nextCells;
        };
        const onInputChange = (index, txt) => {
            var _a, _b;
            const nextCells = patchValue(index, txt);
            const nextIndex = Math.min(index + Math.max(txt.length, 1), props.length - 1);
            if (nextIndex !== index && nextCells[index] !== undefined) {
                (_b = (_a = inputsRef.value[nextIndex]) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a);
            }
            triggerValueCellsChange(nextCells);
        };
        const onActiveChange = (nextIndex) => {
            var _a, _b;
            if (nextIndex >= 0 && nextIndex < props.length) {
                (_b = (_a = inputsRef.value[nextIndex]) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a);
            }
        };
        const onInputFocus = (event, index) => {
            var _a, _b;
            for (let i = 0; i < index; i += 1) {
                if (!valueCells.value[i]) {
                    (_b = (_a = inputsRef.value[i]) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a);
                    break;
                }
            }
            emit('focus', event);
        };
        return () => {
            const pre = prefixCls.value;
            const length = props.length;
            const nodes = [];
            for (let index = 0; index < length; index += 1) {
                const singleValue = valueCells.value[index] || '';
                nodes.push(<OTPInput key={`otp-${index}`} ref={(el) => {
                        if (el)
                            inputsRef.value[index] = el;
                    }} index={index} size={mergedSize.value} status={mergedStatus.value} disabled={props.disabled} mask={props.mask} type={props.type} inputMode={props.inputMode} autoComplete={props.autoComplete} autofocus={index === 0 && props.autofocus} bordered={props.bordered} class={`${pre}-input`} value={singleValue} onChange={onInputChange} onActiveChange={onActiveChange} onFocus={(e) => onInputFocus(e, index)}/>);
                if (index < length - 1 && props.separator != null) {
                    const sep = typeof props.separator === 'function' ? props.separator(index) : props.separator;
                    if (sep != null && sep !== false) {
                        nodes.push(<span key={`sep-${index}`} class={`${pre}-separator`}>
                {sep}
              </span>);
                    }
                }
            }
            return wrapSSR(<div {...attrs} ref={containerRef} role="group" class={classNames(pre, hashId.value, props.rootClassName, attrs.class, {
                    [`${pre}-sm`]: mergedSize.value === 'small',
                    [`${pre}-lg`]: mergedSize.value === 'large',
                    [`${pre}-rtl`]: direction.value === 'rtl',
                })}>
          {nodes}
        </div>);
        };
    },
});
export default OTP;
